/**
 * 公車路線識別（與 backend/utils/bus-route-identity.js 保持同步）
 * 內部：TDX 縣市前綴 + 穩定 ID；畫面號碼仍顯示 304。
 */

import { normalizeBusCityCode } from './bus-route-display.js';

const TDX_CITY_PREFIX_RE = /^([A-Z]{3})/i;

export function normalizeBusRouteToken(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

export function busRouteOperatorCode(detail = {}) {
    const city = detail.h_city_code || detail.city_code;
    if (city && /^[A-Za-z]{3}$/.test(String(city))) return normalizeBusCityCode(city);
    const uid = detail.h_ref_codes?.tdx_route_uid
        || detail.tdx_route_uid
        || detail.h_route_family_id
        || '';
    const fromUid = String(uid).match(TDX_CITY_PREFIX_RE);
    if (fromUid) return normalizeBusCityCode(fromUid[1]);
    const rid = String(detail.route_id || detail.h_route_key || detail.id || '');
    const fromRid = rid.match(TDX_CITY_PREFIX_RE);
    return fromRid ? normalizeBusCityCode(fromRid[1]) : '';
}

export function busRouteStableIds(detail = {}) {
    const ids = new Set();
    const codes = detail.h_ref_codes || {};
    for (const field of [
        detail.h_uid,
        detail.h_route_key,
        detail.route_id,
        detail.id,
        detail.h_route_family_id,
        codes.tdx_route_uid,
        codes.tdx_sub_route_uid,
        detail.tdx_route_uid,
        detail.tdx_sub_route_uid
    ]) {
        const s = String(field ?? '').trim();
        if (s) ids.add(s);
    }
    return ids;
}

export function busRouteRefTokens(detail = {}) {
    const tokens = new Set();
    for (const field of [detail.ref, detail.h_ref, detail.h_name, detail.name, detail.h_route_name]) {
        const base = normalizeBusRouteToken(field);
        if (!base) continue;
        tokens.add(base);
        if (base.endsWith('區')) tokens.add(base.replace(/區$/, ''));
        else tokens.add(`${base}區`);
    }
    return tokens;
}

export function busRouteIdentityTokens(detail = {}) {
    const tokens = busRouteRefTokens(detail);
    for (const id of busRouteStableIds(detail)) {
        tokens.add(normalizeBusRouteToken(id));
    }
    return tokens;
}

function refTokensOverlap(a, b) {
    const ta = busRouteRefTokens(a);
    const tb = busRouteRefTokens(b);
    for (const t of ta) {
        if (tb.has(t)) return true;
    }
    return false;
}

export function busRouteIdentityPayload(detail = {}) {
    const codes = detail.h_ref_codes || {};
    return {
        route_id: detail.route_id,
        id: detail.id,
        ref: detail.ref,
        h_ref: detail.h_ref,
        name: detail.name,
        h_name: detail.h_name,
        h_route_name: detail.h_route_name,
        h_city_code: detail.h_city_code || detail.city_code,
        h_route_family_id: detail.h_route_family_id,
        h_route_key: detail.h_route_key,
        h_uid: detail.h_uid,
        tdx_route_uid: detail.tdx_route_uid || codes.tdx_route_uid,
        tdx_sub_route_uid: detail.tdx_sub_route_uid || codes.tdx_sub_route_uid,
        h_ref_codes: codes.tdx_route_uid || codes.tdx_sub_route_uid ? codes : detail.h_ref_codes
    };
}

export function busRouteDetailsMatch(a, b) {
    if (!a || !b) return false;
    const opA = busRouteOperatorCode(a);
    const opB = busRouteOperatorCode(b);
    // 防撞：兩邊都有縣市碼且不同 → 永不視為同一路線（避免裸 705 跨縣撞號）
    if (opA && opB && opA !== opB) return false;

    const idsA = busRouteStableIds(a);
    const idsB = busRouteStableIds(b);
    for (const id of idsA) {
        if (idsB.has(id)) return true;
    }
    if (opA && opB && opA === opB && refTokensOverlap(a, b)) return true;
    return false;
}

/** by-ids 查詢用：只收帶縣市前綴／夠長的穩定 ID，禁止裸號碼 705 */
export function isStrongBusRouteId(id) {
    const s = String(id ?? '').trim();
    if (!s) return false;
    if (/^[A-Za-z]{3}/.test(s)) return true;
    if (s.length >= 8 && /[A-Za-z]/.test(s)) return true;
    return false;
}

export function busRouteFetchIds(detail = {}) {
    const out = [];
    for (const id of busRouteStableIds(detail)) {
        if (isStrongBusRouteId(id)) out.push(String(id).trim());
    }
    return out;
}
